/**
 * Organisation Chart — drill-down focus view.
 *
 * Shows one "level" at a time as a responsive card grid — no horizontal
 * scrolling. Click any card that has reports to drill into their team.
 * Navigate back via breadcrumb or the up-arrow button.
 */

import { useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { employeeInfoApi } from '../api/employeeInfoApi'
import type { OrgChartNode } from '../types/employee-info.types'

// ---------------------------------------------------------------------------
// Avatar helpers
// ---------------------------------------------------------------------------

const AVATAR_COLORS = [
  ['bg-blue-100 text-blue-700', 'ring-blue-200'],
  ['bg-indigo-100 text-indigo-700', 'ring-indigo-200'],
  ['bg-purple-100 text-purple-700', 'ring-purple-200'],
  ['bg-pink-100 text-pink-700', 'ring-pink-200'],
  ['bg-rose-100 text-rose-700', 'ring-rose-200'],
  ['bg-orange-100 text-orange-700', 'ring-orange-200'],
  ['bg-amber-100 text-amber-700', 'ring-amber-200'],
  ['bg-teal-100 text-teal-700', 'ring-teal-200'],
  ['bg-cyan-100 text-cyan-700', 'ring-cyan-200'],
  ['bg-emerald-100 text-emerald-700', 'ring-emerald-200'],
]

function avatarClasses(id: string) {
  let h = 0
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) | 0
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

function initials(name: string) {
  return name
    .split(' ')
    .slice(0, 2)
    .map((w) => w[0] ?? '')
    .join('')
    .toUpperCase()
}

// ---------------------------------------------------------------------------
// Flatten tree for search
// ---------------------------------------------------------------------------

function flattenTree(nodes: OrgChartNode[]): OrgChartNode[] {
  return nodes.flatMap((n) => [n, ...flattenTree(n.children)])
}

// Count total descendants
function totalDescendants(node: OrgChartNode): number {
  return node.children.reduce((acc, c) => acc + 1 + totalDescendants(c), 0)
}

// ---------------------------------------------------------------------------
// Employee card
// ---------------------------------------------------------------------------

interface CardProps {
  node: OrgChartNode
  size?: 'sm' | 'md' | 'lg'
  onClick?: () => void
  isManager?: boolean
}

function EmployeeCard({ node, size = 'md', onClick, isManager }: CardProps) {
  const [bg, ring] = avatarClasses(node.id)
  const canDrill = node.children.length > 0
  const reportCount = node.children.length

  const avatarSize =
    size === 'lg' ? 'w-16 h-16 text-xl' : size === 'sm' ? 'w-10 h-10 text-sm' : 'w-12 h-12 text-base'

  return (
    <div
      onClick={canDrill || isManager ? onClick : undefined}
      role={canDrill || isManager ? 'button' : undefined}
      tabIndex={canDrill || isManager ? 0 : undefined}
      onKeyDown={
        canDrill || isManager
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') onClick?.()
            }
          : undefined
      }
      className={`
        group relative bg-white rounded-2xl border-2 p-4 flex flex-col items-center text-center
        transition-all duration-200
        ${isManager
          ? 'border-blue-300 shadow-md shadow-blue-100 w-full max-w-xs mx-auto'
          : canDrill
          ? 'border-gray-200 hover:border-blue-400 hover:shadow-md cursor-pointer'
          : 'border-gray-200'}
      `}
    >
      {/* Manager label */}
      {isManager && (
        <span className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-blue-600 text-white text-xs font-semibold rounded-full shadow">
          Manager
        </span>
      )}

      {/* Avatar */}
      <div
        className={`
          ${avatarSize} rounded-full flex items-center justify-center font-bold
          ring-2 ${ring} ${bg} mb-3 shrink-0
        `}
      >
        {initials(node.name)}
      </div>

      {/* Name */}
      <p className={`font-semibold text-gray-900 leading-snug ${size === 'lg' ? 'text-base' : 'text-sm'}`}>
        {node.name}
      </p>

      {/* Job title */}
      {node.job_title && (
        <p className="text-xs text-gray-500 mt-0.5 leading-snug line-clamp-2">
          {node.job_title}
        </p>
      )}

      {/* Division */}
      {node.division && (
        <span className="mt-2 px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 text-xs truncate max-w-full">
          {node.division}
        </span>
      )}

      {/* Direct reports badge */}
      {reportCount > 0 && (
        <div className="mt-3 flex items-center gap-1 text-xs text-blue-600 font-medium">
          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
              d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          {reportCount} direct report{reportCount !== 1 ? 's' : ''}
        </div>
      )}

      {/* Drill-down arrow */}
      {canDrill && !isManager && (
        <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity text-blue-500">
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Search result row
// ---------------------------------------------------------------------------

interface SearchRowProps {
  node: OrgChartNode
  onNavigate: () => void
}

function SearchRow({ node, onNavigate }: SearchRowProps) {
  const [bg] = avatarClasses(node.id)
  return (
    <button
      onClick={onNavigate}
      className="w-full flex items-center gap-3 px-4 py-3 hover:bg-gray-50 rounded-xl transition-colors text-left"
    >
      <div
        className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ${bg}`}
      >
        {initials(node.name)}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-medium text-gray-900 truncate">{node.name}</p>
        <p className="text-xs text-gray-500 truncate">
          {[node.job_title, node.division].filter(Boolean).join(' · ')}
        </p>
      </div>
      {node.children.length > 0 && (
        <span className="ml-auto shrink-0 text-xs text-blue-600 font-medium">
          {node.children.length} reports
        </span>
      )}
    </button>
  )
}

// ---------------------------------------------------------------------------
// Full-tree node (expand-all mode) — proper ├/└ connectors, no horizontal scroll
// ---------------------------------------------------------------------------

interface FullTreeNodeProps {
  node: OrgChartNode
  depth?: number
}

function FullTreeNode({ node, depth = 0 }: FullTreeNodeProps) {
  const [bg, ring] = avatarClasses(node.id)
  const hasChildren = node.children.length > 0

  return (
    <div>
      {/* Row — avatar center is at py-2 (4px) + half-avatar (18px) = 22px from top */}
      <div className="flex items-center gap-3 py-2 min-w-0">
        <div
          className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold shrink-0 ring-2 ${ring} ${bg}`}
        >
          {initials(node.name)}
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-gray-900 truncate">{node.name}</p>
          {(node.job_title || node.division) && (
            <p className="text-xs text-gray-500 truncate">
              {[node.job_title, node.division].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        {hasChildren && (
          <span className="shrink-0 text-xs text-blue-600 font-medium whitespace-nowrap ml-2">
            {node.children.length} report{node.children.length !== 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* Children: vertical line anchored at parent-avatar center (ml-[18px]) */}
      {hasChildren && (
        <div className="ml-[18px]">
          {node.children.map((child, idx) => {
            const isLast = idx === node.children.length - 1
            return (
              <div key={child.id} className="relative pl-7">
                {/* Vertical segment — full height for ├, half height for └ */}
                <div
                  className={`absolute left-0 w-0.5 bg-gray-300 ${
                    isLast ? 'top-0 h-[22px]' : 'top-0 bottom-0'
                  }`}
                />
                {/* Horizontal connector at avatar-center height (21px from top) */}
                <div className="absolute left-0 top-[21px] w-7 h-0.5 bg-gray-300" />
                <FullTreeNode node={child} depth={depth + 1} />
              </div>
            )
          })}
        </div>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export default function OrgChartPage() {
  // path = breadcrumb stack; empty = company root
  const [path, setPath] = useState<OrgChartNode[]>([])
  const [search, setSearch] = useState('')
  const [expandAll, setExpandAll] = useState(false)

  const { data, isLoading, error } = useQuery({
    queryKey: ['org-chart'],
    queryFn: () => employeeInfoApi.getOrgChart(),
    staleTime: 5 * 60 * 1000,
  })

  const roots = data?.data ?? []
  const allEmployees = useMemo(() => flattenTree(roots), [roots])

  // Current focus
  const focused = path[path.length - 1] ?? null
  const visibleChildren = focused ? focused.children : roots

  // Search
  const searchResults = useMemo(() => {
    const q = search.trim().toLowerCase()
    if (!q) return []
    return allEmployees.filter(
      (n) =>
        n.name.toLowerCase().includes(q) ||
        n.job_title.toLowerCase().includes(q) ||
        n.division.toLowerCase().includes(q) ||
        n.department.toLowerCase().includes(q),
    )
  }, [search, allEmployees])

  const inSearchMode = search.trim().length > 0

  // Navigation
  const drillDown = (node: OrgChartNode) => {
    setPath((p) => [...p, node])
    setSearch('')
  }

  const navigateTo = (index: number) => {
    setPath((p) => p.slice(0, index + 1))
  }

  const goUp = () => setPath((p) => p.slice(0, -1))
  const goRoot = () => setPath([])

  // Navigate to a search result by rebuilding path from tree
  const navigateToNode = (target: OrgChartNode) => {
    // BFS to find path from roots to target
    const findPath = (
      nodes: OrgChartNode[],
      targetId: string,
      current: OrgChartNode[],
    ): OrgChartNode[] | null => {
      for (const n of nodes) {
        const next = [...current, n]
        if (n.id === targetId) return next
        const found = findPath(n.children, targetId, next)
        if (found) return found
      }
      return null
    }
    const found = findPath(roots, target.id, [])
    if (found) {
      setPath(found)
      setSearch('')
    }
  }

  // Stats
  const totalEmployees = allEmployees.length

  // ---------------------------------------------------------------------------

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      {/* ── Header ── */}
      <div className="bg-white border-b border-gray-200 px-6 py-4 shrink-0">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3">
          {/* Title + stats */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2">
              <h1 className="text-xl font-bold text-gray-900">Org Chart</h1>
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full font-medium">
                {totalEmployees} employees
              </span>
            </div>

            {/* Breadcrumb (drill-down mode only) */}
            <nav className={`flex items-center gap-1 mt-1 flex-wrap ${expandAll ? 'invisible' : ''}`}>
              <button
                onClick={goRoot}
                className="text-sm text-blue-600 hover:underline font-medium shrink-0"
              >
                Company
              </button>
              {path.map((node, i) => (
                <span key={node.id} className="flex items-center gap-1 shrink-0">
                  <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                  <button
                    onClick={() => navigateTo(i)}
                    className={`text-sm hover:underline ${
                      i === path.length - 1 ? 'text-gray-700 font-medium' : 'text-blue-600'
                    }`}
                  >
                    {node.name.split(' ')[0]}
                  </button>
                </span>
              ))}
            </nav>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2 shrink-0">
            {/* Expand all toggle */}
            <button
              onClick={() => { setExpandAll((v) => !v); setSearch('') }}
              title={expandAll ? 'Switch to drill-down view' : 'Show full org chart at once'}
              className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg border transition-colors ${
                expandAll
                  ? 'bg-blue-600 border-blue-600 text-white hover:bg-blue-700'
                  : 'border-gray-300 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              {expandAll ? 'Collapse' : 'Expand all'}
            </button>

            {/* Up button (drill-down mode only) */}
            {!expandAll && path.length > 0 && (
              <button
                onClick={goUp}
                title="Go up one level"
                className="flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                </svg>
                Up
              </button>
            )}

            {/* Search */}
            <div className="relative">
              <svg
                className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none"
                fill="none" stroke="currentColor" viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder="Search employee…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
              />
              {search && (
                <button
                  onClick={() => setSearch('')}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* ── Body ── */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Loading */}
        {isLoading && (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-blue-600" />
          </div>
        )}

        {/* Error */}
        {error && (
          <div className="flex items-center justify-center h-64 text-red-600 text-sm">
            Failed to load org chart. Please refresh.
          </div>
        )}

        {/* Expand-all view */}
        {!isLoading && !error && expandAll && !inSearchMode && (
          <div className="max-w-3xl mx-auto bg-white rounded-2xl border border-gray-200 shadow-sm px-6 py-2 divide-y divide-gray-100">
            {roots.map((root) => (
              <div key={root.id} className="py-2">
                <FullTreeNode node={root} depth={0} />
              </div>
            ))}
          </div>
        )}

        {/* Search results */}
        {!isLoading && !error && inSearchMode && (
          <div className="bg-white rounded-2xl border border-gray-200 divide-y divide-gray-100 max-w-lg mx-auto shadow-sm">
            {searchResults.length === 0 && (
              <p className="text-center text-sm text-gray-500 py-8">No results for "{search}"</p>
            )}
            {searchResults.map((node) => (
              <SearchRow
                key={node.id}
                node={node}
                onNavigate={() => navigateToNode(node)}
              />
            ))}
          </div>
        )}

        {/* Drill-down view */}
        {!isLoading && !error && !inSearchMode && !expandAll && (
          <div className="max-w-5xl mx-auto space-y-8">
            {/* Manager card (when drilled in) */}
            {focused && (
              <div className="flex flex-col items-center">
                <EmployeeCard
                  node={focused}
                  size="lg"
                  isManager
                  onClick={goUp}
                />
                {/* Connector line */}
                <div className="w-0.5 h-8 bg-gray-200 mt-2" />
                <p className="text-xs text-gray-400 font-medium uppercase tracking-wide -mt-1">
                  {visibleChildren.length} direct report{visibleChildren.length !== 1 ? 's' : ''}
                  {totalDescendants(focused) > visibleChildren.length
                    ? ` · ${totalDescendants(focused)} in team`
                    : ''}
                </p>
              </div>
            )}

            {/* Root label */}
            {!focused && (
              <div className="text-center">
                <p className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Leadership
                </p>
              </div>
            )}

            {/* Children grid */}
            {visibleChildren.length > 0 ? (
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
                {visibleChildren.map((child) => (
                  <EmployeeCard
                    key={child.id}
                    node={child}
                    size="md"
                    onClick={() => drillDown(child)}
                  />
                ))}
              </div>
            ) : (
              <div className="text-center py-12 text-gray-400">
                <svg className="w-12 h-12 mx-auto mb-3 opacity-40" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                    d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                <p className="text-sm">No direct reports</p>
              </div>
            )}

            {/* Hint */}
            {visibleChildren.some((c) => c.children.length > 0) && (
              <p className="text-center text-xs text-gray-400">
                Click a card with reports to drill into their team
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
